import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminUsersApi, adminGradesApi, adminDivisionsApi } from '@/api/admin';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { subjectsAPI, chaptersAPI } from '@/api/curriculum';
import { getUserData } from '@/utils/auth';
import { Edit, Trash2, CheckCircle, XCircle, RefreshCw, Loader2, Plus } from 'lucide-react';

const pageSize = 10;

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const selectStyle = { backgroundColor: 'hsl(var(--input-bg))', color: 'white' };

export default function AdminUsersPage({ userType }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [grades, setGrades] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: 'all', grade: 'all', division: 'all' });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ full_name: '', username: '', email: '', phone_no: '', grade: '', division: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState({});
  const [resettingPassword, setResettingPassword] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', username: '', email: '', phone_no: '', grade: '', division: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [confirmResetUser, setConfirmResetUser] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const schoolId = useMemo(() => getUserData('admin')?.school_id, []);

  const isStudent = userType === 'student';

  // Filter divisions based on selected grade
  const filteredDivisions = useMemo(() => {
    if (!form.grade) return [];
    return divisions.filter((d) => String(d.grade) === String(form.grade));
  }, [divisions, form.grade]);

  const filteredDivisionsForFilter = useMemo(() => {
    if (filters.grade === 'all') return divisions;
    return divisions.filter((d) => String(d.grade) === String(filters.grade));
  }, [divisions, filters.grade]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [gradesRes, divisionsRes] = await Promise.all([
          adminGradesApi.list(),
          adminDivisionsApi.list(),
        ]);
        setGrades(gradesRes.data || []);
        setDivisions(divisionsRes.data?.results || divisionsRes.data || []);
      } catch (err) {
        console.error('Failed to load grade/division metadata', err);
      }
    };

    loadMeta();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!schoolId) {
        setError('School not found for this admin account.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params = {
          user_type: userType,
          page,
          page_size: pageSize,
        };

        if (filters.search) params.search = filters.search;
        if (filters.status !== 'all') params.is_active = filters.status === 'active';
        if (filters.grade !== 'all') params.grade = filters.grade;
        if (filters.division !== 'all') params.division = filters.division;

        const res = await adminUsersApi.list(params);
        const items = res.data.results || res.data || [];
        setRecords(items);
        setTotal(res.data.count ?? items.length ?? 0);
      } catch (err) {
        console.error('Failed to load users', err);
        const status = err?.response?.status;
        if (status === 403) {
          setError('Access denied. Please check your permissions.');
        } else if (status === 401) {
          setError('Your session has expired. Please log in again.');
        } else {
          setError('Unable to load users right now.');
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [filters, page, schoolId, userType]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleCreate = async () => {
    if (!schoolId) return;
    if (isStudent && !form.grade) {
      setError('Please select a grade for the student.');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        full_name: form.full_name,
        username: form.username,
        email: form.email || '',
        phone_no: form.phone_no || '',
      };

      if (isStudent) {
        payload.grade = form.grade || null;
        if (form.division) payload.division = form.division;
        await adminUsersApi.createStudent(payload);
      } else {
        await adminUsersApi.createTeacher(payload);
      }

      setForm({ full_name: '', username: '', email: '', phone_no: '', grade: '', division: '' });
      setShowCreateForm(false);
      setPage(1);
      setFilters((prev) => ({ ...prev }));
      try { trackEvent('admin_user_created', attachUserProps({ user_type: userType, page: 'admin_users' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to create user', err);
      setError(err.response?.data?.detail || 'Unable to create account.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    setTogglingStatus((prev) => ({ ...prev, [userId]: true }));
    try {
      await adminUsersApi.toggleActive(userId, { is_active: !currentStatus });
      setRecords((prev) => 
        prev.map((r) => 
          r.id === userId ? { ...r, user: { ...r.user, is_active: !currentStatus }, is_active: !currentStatus } : r
        )
      );
    } catch (err) {
      console.error('Failed to toggle status', err);
      setError(err.response?.data?.error || 'Could not update user status.');
    } finally {
      setTogglingStatus((prev) => ({ ...prev, [userId]: false }));
      try { trackEvent('admin_user_status_toggled', attachUserProps({ user_id: userId, new_status: !currentStatus, page: 'admin_users' })); } catch (e) {}
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDeleteUser) return;
    try {
      await adminUsersApi.delete(confirmDeleteUser.id);
      setRecords((prev) => prev.filter((r) => r.id !== confirmDeleteUser.id));
      try { trackEvent('admin_user_deleted', attachUserProps({ user_id: confirmDeleteUser.id, page: 'admin_users' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to delete user', err);
      setError('Could not delete user.');
    } finally {
      setConfirmDeleteUser(null);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || '',
      username: user.username || user.user?.username || '',
      email: user.email || user.user?.email || '',
      phone_no: user.phone_no || '',
      grade: user.grade?.id || user.grade || user.grade_id || user.grade_display?.id || '',
      division: user.division?.id || user.division || user.division_id || user.division_display?.id || '',
    });
    if (user.user_type === 'teacher') {
      loadAssignments(user.id);
    } else {
      resetAssignmentState();
    }
    setEditModalOpen(true);
    try { trackEvent('admin_open_edit_user', attachUserProps({ user_id: user.id, page: 'admin_users' })); } catch (e) {}
  };

  const resetAssignmentState = () => {
    setAssignments([]);
    setEditingAssignmentId(null);
  };

  const loadAssignments = async (userId) => {
    resetAssignmentState();
    setAssignmentsLoading(true);
    try {
      const res = await adminUsersApi.getAssignments(userId);
      const existing = res.data?.assignments || [];

      const hydrated = await Promise.all(
        existing.map(async (item, idx) => {
          const gradeId = item.grade ? String(item.grade) : '';
          const subjectId = item.subject ? String(item.subject) : '';
          const subjectsOptions = gradeId ? await subjectsAPI.list({ grade: gradeId }) : [];
          const chaptersOptions = subjectId ? await chaptersAPI.list({ subject: subjectId }) : [];
          const subjectName = subjectsOptions.find((s) => String(s.id) === subjectId)?.subject_name || item.subject_name || '';
          const gradeName = grades.find((g) => String(g.id) === gradeId)?.grade_name || item.grade_name || '';

          return {
            id: `existing-${idx}`,
            grade: gradeId,
            subject: subjectId,
            chapters: item.chapters?.map((c) => String(c.id)) || [],
            divisions: item.divisions?.map((d) => String(d.id)) || [],
            subjectsOptions,
            chaptersOptions,
            subjectName,
            gradeName,
            loadingSubjects: false,
            loadingChapters: false,
            isExisting: true,
          };
        })
      );

      const rows = hydrated.length > 0 ? hydrated : [{
        id: `new-${Date.now()}`,
        grade: '',
        subject: '',
        chapters: [],
        divisions: [],
        subjectsOptions: [],
        chaptersOptions: [],
        loadingSubjects: false,
        loadingChapters: false,
        isExisting: false,
      }];

      setAssignments(rows);
      setEditingAssignmentId(null);
    } catch (err) {
      console.error('Failed to load assignments', err);
      setAssignments([
        {
          id: `new-${Date.now()}`,
          grade: '',
          subject: '',
          chapters: [],
          divisions: [],
          subjectsOptions: [],
          chaptersOptions: [],
          loadingSubjects: false,
          loadingChapters: false,
          isExisting: false,
        },
      ]);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const loadSubjectsForAssignment = async (assignmentId, gradeId) => {
    setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, loadingSubjects: true } : a));
    if (!gradeId) {
      setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, subjectsOptions: [], subject: '', chapters: [], chaptersOptions: [], loadingSubjects: false } : a));
      return;
    }
    try {
      const data = await subjectsAPI.list({ grade: gradeId });
      setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, subjectsOptions: data || [], subject: '', chapters: [], chaptersOptions: [], loadingSubjects: false } : a));
    } catch (err) {
      console.error('Failed to load subjects', err);
      setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, subjectsOptions: [], subject: '', chapters: [], chaptersOptions: [], loadingSubjects: false } : a));
    }
  };

  const loadChaptersForAssignment = async (assignmentId, subjectId) => {
    setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, loadingChapters: true } : a));
    if (!subjectId) {
      setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, chapters: [], chaptersOptions: [], loadingChapters: false } : a));
      return;
    }
    try {
      const data = await chaptersAPI.list({ subject: subjectId });
      setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, chaptersOptions: data || [], chapters: [], loadingChapters: false } : a));
    } catch (err) {
      console.error('Failed to load chapters', err);
      setAssignments((prev) => prev.map((a) => a.id === assignmentId ? { ...a, chaptersOptions: [], chapters: [], loadingChapters: false } : a));
    }
  };

  const addAssignmentRow = () => {
    const newId = `new-${Date.now()}`;
    setAssignments((prev) => [
      ...prev,
      {
        id: newId,
        grade: '',
        subject: '',
        chapters: [],
        divisions: [],
        subjectsOptions: [],
        chaptersOptions: [],
        loadingSubjects: false,
        loadingChapters: false,
        isExisting: false,
      },
    ]);
    setEditingAssignmentId(newId);
  };

  const removeAssignmentRow = (rowId) => {
    setAssignments((prev) => prev.filter((a) => a.id !== rowId));
  };

  const handleEditAssignment = (assignmentId) => {
    setEditingAssignmentId(assignmentId);
  };

  const handleCancelEdit = () => {
    if (assigningUser) {
      loadAssignments(assigningUser.id);
    }
    setEditingAssignmentId(null);
  };

  const handleAssignmentGradeChange = async (assignmentId, gradeId) => {
    updateAssignmentField(assignmentId, { grade: gradeId, subject: '', chapters: [], divisions: [], chaptersOptions: [], subjectsOptions: [] });
    await loadSubjectsForAssignment(assignmentId, gradeId);
    setEditingAssignmentId((prev) => prev || assignmentId);
  };

  const handleAssignmentSubjectChange = async (assignmentId, subjectId) => {
    updateAssignmentField(assignmentId, { subject: subjectId, chapters: [] });
    await loadChaptersForAssignment(assignmentId, subjectId);
    setEditingAssignmentId((prev) => prev || assignmentId);
  };

  const toggleDivisionSelection = (assignmentId, divisionId) => {
    const divisionKey = String(divisionId);
    setAssignments((prev) => prev.map((a) => {
      if (a.id !== assignmentId) return a;
      const selected = a.divisions || [];
      const exists = selected.includes(divisionKey);
      return { ...a, divisions: exists ? selected.filter((d) => d !== divisionKey) : [...selected, divisionKey] };
    }));
  };

  const toggleChapterSelection = (assignmentId, chapterId) => {
    const chapterKey = String(chapterId);
    setAssignments((prev) => prev.map((a) => {
      if (a.id !== assignmentId) return a;
      const selected = a.chapters || [];
      const exists = selected.includes(chapterKey);
      return { ...a, chapters: exists ? selected.filter((c) => c !== chapterKey) : [...selected, chapterKey] };
    }));
  };

  const handleSelectAllDivisions = (assignmentId) => {
    const assignment = assignments.find((a) => a.id === assignmentId);
    if (!assignment) return;
    const available = divisions.filter((d) => {
      const gradeValue = d.grade ?? d.grade_id;
      return assignment.grade ? String(gradeValue) === String(assignment.grade) : false;
    });
    const allSelected = available.length > 0 && available.every((d) => (assignment.divisions || []).includes(String(d.id)));
    const next = allSelected ? [] : available.map((d) => String(d.id));
    updateAssignmentField(assignmentId, { divisions: next });
  };

  const handleSelectAllChapters = (assignmentId) => {
    const assignment = assignments.find((a) => a.id === assignmentId);
    if (!assignment) return;
    const available = assignment.chaptersOptions || [];
    const allSelected = available.length > 0 && available.every((c) => (assignment.chapters || []).includes(String(c.id)));
    const next = allSelected ? [] : available.map((c) => String(c.id));
    updateAssignmentField(assignmentId, { chapters: next });
  };

  const updateAssignmentField = (rowId, updates) => {
    setAssignments((prev) => prev.map((a) => a.id === rowId ? { ...a, ...updates } : a));
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    if (isStudent && !editForm.grade) {
      setError('Please select a grade for the student.');
      return;
    }

    setSavingEdit(true);
    try {
      const payload = {
        full_name: editForm.full_name,
        username: editForm.username,
        email: editForm.email,
        phone_no: editForm.phone_no,
      };

      if (isStudent) {
        payload.grade = editForm.grade || null;
        payload.division = editForm.division || null;
      }

      await adminUsersApi.update(editingUser.id, payload);

      if (editingUser.user_type === 'teacher' && assignments.length > 0) {
        const hasAssignmentData = assignments.some((a) => a.grade || a.subject || (a.chapters?.length || 0) > 0 || (a.divisions?.length || 0) > 0);
        if (hasAssignmentData) {
          const formatted = assignments
            .filter((a) => a.grade && a.subject && (a.chapters?.length || 0) > 0)
            .map((a) => ({
              grade: Number(a.grade),
              subject: Number(a.subject),
              chapters: (a.chapters || []).map((c) => Number(c)),
              divisions: (a.divisions || []).map((d) => Number(d)),
            }));

          if (formatted.length === 0) {
            setError('Add at least one assignment with grade, subject, and chapters.');
            setSavingEdit(false);
            return;
          }

          await adminUsersApi.setAssignments(editingUser.id, { assignments: formatted });
        }
      }
      setFilters((prev) => ({ ...prev }));
      setEditModalOpen(false);
      setEditingUser(null);
      try { trackEvent('admin_user_updated', attachUserProps({ user_id: editingUser.id, page: 'admin_users' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to update user', err);
      setError(err.response?.data?.detail || 'Unable to update user.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResetPasswordConfirmed = async () => {
    if (!confirmResetUser) return;
    setResettingPassword((prev) => ({ ...prev, [confirmResetUser.id]: true }));
    try {
      await adminUsersApi.resetPassword(confirmResetUser.id);
      try { trackEvent('admin_user_password_reset', attachUserProps({ user_id: confirmResetUser.id, page: 'admin_users' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to reset password', err);
      setError(err.response?.data?.detail || 'Could not reset password.');
    } finally {
      setResettingPassword((prev) => ({ ...prev, [confirmResetUser.id]: false }));
      setConfirmResetUser(null);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const res = await adminUsersApi.downloadSample(userType);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${userType}-import-sample.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      try { trackEvent('admin_user_sample_downloaded', attachUserProps({ user_type: userType, page: 'admin_users' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to download sample', err);
      setError('Could not download sample file.');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      setError('Select an .xlsx file to upload.');
      return;
    }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const res = await adminUsersApi.bulkImport(userType, bulkFile);
      setBulkResult(res.data);
      setPage(1);
      setFilters((prev) => ({ ...prev }));
      try { trackEvent('admin_user_bulk_import', attachUserProps({ user_type: userType, processed: res.data?.processed, created: res.data?.created, failed: res.data?.failed, page: 'admin_users' })); } catch (e) {}
    } catch (err) {
      console.error('Bulk import failed', err);
      setError(err.response?.data?.error || 'Bulk import failed.');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleAssignmentsSave = async () => {
    if (!assigningUser) return;

    const formatted = assignments
      .filter((a) => a.grade && a.subject && (a.chapters?.length || 0) > 0)
      .map((a) => ({
        grade: Number(a.grade),
        subject: Number(a.subject),
        chapters: (a.chapters || []).map((c) => Number(c)),
        divisions: (a.divisions || []).map((d) => Number(d)),
      }));

    if (formatted.length === 0) {
      setError('Add at least one assignment with grade, subject, and chapters.');
      return;
    }

    setSavingAssignments(true);
    try {
      await adminUsersApi.setAssignments(assigningUser.id, { assignments: formatted });
      await loadAssignments(assigningUser.id);
      setAssignModalOpen(false);
      setAssigningUser(null);
      setEditingAssignmentId(null);
        try { trackEvent('admin_user_assignments_saved', attachUserProps({ user_id: assigningUser.id, assignment_count: formatted.length, page: 'admin_users' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to save assignments', err);
      setError(err.response?.data?.detail || 'Could not save assignments.');
    } finally {
      setSavingAssignments(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto w-full space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground capitalize">{userType} management</h1>
            <p className="text-muted-foreground text-sm">Manage {userType}s for your school. Filters and results are scoped to this school.</p>
          </div>
          <div className="flex items-center gap-2">
            {showCreateForm && (
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                Close quick create
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleDownloadSample} className="text-white border-[hsl(var(--card-border))] hover:bg-blue-600 hover:text-white hover:border-blue-600">
              Download sample
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setBulkModalOpen(true); try { trackEvent('admin_open_bulk_import', attachUserProps({ user_type: userType, page: 'admin_users' })); } catch (e) {} }} className="text-white border-[hsl(var(--card-border))] hover:bg-blue-600 hover:text-white hover:border-blue-600">
              Bulk import
            </Button>
            <Button onClick={() => { setShowCreateForm(true); try { trackEvent('admin_open_create_user', attachUserProps({ user_type: userType, page: 'admin_users' })); } catch (e) {} }}>
              Create {userType}
            </Button>
          </div>
        </div>

        {showCreateForm && (
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardHeader>
              <CardTitle className="text-foreground text-lg">Quick create</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">Full name</Label>
                <Input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Username</Label>
                <Input value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} placeholder="jane.doe" />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Email (optional)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="jane@school.edu" />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Phone (optional)</Label>
                <Input value={form.phone_no} onChange={(e) => setForm((p) => ({ ...p, phone_no: e.target.value }))} placeholder="+1-555-1234" />
              </div>
              {isStudent && (
                <>
                  <div>
                    <Label className="text-sm text-muted-foreground">Grade</Label>
                    <select 
                      style={selectStyle}
                      className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                      value={form.grade} 
                      onChange={(e) => setForm((p) => ({ ...p, grade: e.target.value, division: '' }))}
                    >
                      <option value="">Select grade</option>
                      {grades.map((g) => (
                        <option key={g.id} value={g.id}>{g.grade_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Division (optional)</Label>
                    <select 
                      style={selectStyle}
                      disabled={!form.grade}
                      className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" 
                      value={form.division} 
                      onChange={(e) => setForm((p) => ({ ...p, division: e.target.value }))}
                    >
                      <option value="">Select division</option>
                      {filteredDivisions.map((d) => (
                        <option key={d.id} value={d.id}>{d.division_name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="flex items-end gap-2 col-span-1 md:col-span-2 lg:col-span-4">
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? 'Saving…' : `Save ${userType}`}
                </Button>
                <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <CardHeader>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`grid grid-cols-1 ${isStudent ? 'md:grid-cols-4' : 'md:grid-cols-2'} gap-3`}>
              <Input
                placeholder="Search by name or email"
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value, page: 1 }))}
                className="bg-[hsl(var(--input-bg))] text-foreground"
              />
              <select 
                style={selectStyle}
                className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                value={filters.status} 
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value, page: 1 }))}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {isStudent && (
                <>
                  <select 
                    style={selectStyle}
                    className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                    value={filters.grade} 
                    onChange={(e) => setFilters((p) => ({ ...p, grade: e.target.value, page: 1 }))}
                  >
                    <option value="all">All grades</option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id}>{g.grade_name}</option>
                    ))}
                  </select>
                  <select 
                    style={selectStyle}
                    disabled={filters.grade === 'all'}
                    className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" 
                    value={filters.division} 
                    onChange={(e) => setFilters((p) => ({ ...p, division: e.target.value, page: 1 }))}
                  >
                    <option value="all">All divisions</option>
                    {filteredDivisionsForFilter.map((d) => (
                      <option key={d.id} value={d.id}>{d.division_name}</option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {error && <div className="text-sm text-red-400">{error}</div>}
            {loading ? (
              <div className="text-muted-foreground text-sm">Loading…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-muted-foreground">
                    <tr>
                      <th className="py-2">Name</th>
                      <th className="py-2">Username</th>
                      <th className="py-2">Email</th>
                      <th className="py-2">Phone</th>
                      {isStudent && <th className="py-2">Grade</th>}
                      {isStudent && <th className="py-2">Division</th>}
                      <th className="py-2">Status</th>
                      <th className="py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(var(--card-border))]">
                    {records.map((u) => (
                      <tr key={u.id} className="text-foreground">
                        <td className="py-2 font-semibold">{u.full_name || '—'}</td>
                        <td className="py-2">{u.username || u.user?.username || '—'}</td>
                        <td className="py-2">{u.email || u.user?.email || '—'}</td>
                        <td className="py-2">{u.phone_no || '—'}</td>
                        {isStudent && (
                          <td className="py-2">{u.grade_display?.grade_name || u.grade_name || u.grade?.grade_name || '—'}</td>
                        )}
                        {isStudent && (
                          <td className="py-2">{u.division_display?.division_name || (u.divisions_display?.[0]?.division_name) || '—'}</td>
                        )}
                        <td className="py-2">
                          <button
                            onClick={() => handleToggleStatus(u.id, u.user?.is_active ?? u.is_active)}
                            disabled={togglingStatus[u.id]}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors hover:opacity-80"
                          >
                            {(u.user?.is_active ?? u.is_active) === false ? (
                              <>
                                <XCircle className="w-4 h-4 text-red-400" />
                                Inactive
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 text-green-400" />
                                Active
                              </>
                            )}
                          </button>
                        </td>
                        <td className="py-2">
                          <div className="flex gap-2">
                            {u.user_type === 'teacher' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-400 hover:text-emerald-300"
                                onClick={() => {
                                  setAssigningUser(u);
                                  loadAssignments(u.id);
                                  setAssignModalOpen(true);
                                }}
                                title="Assign classes"
                              >
                                Assign
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-blue-400 hover:text-blue-300"
                              onClick={() => openEditModal(u)}
                              title="Edit profile"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-amber-400 hover:text-amber-300"
                              onClick={() => setConfirmResetUser(u)}
                              disabled={resettingPassword[u.id]}
                              title="Reset password"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-400 hover:text-red-300"
                              onClick={() => setConfirmDeleteUser(u)}
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Page {page} of {totalPages}</span>
              <div className="space-x-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[hsl(var(--card-bg))] text-foreground">
          <DialogHeader>
            <DialogTitle className="text-white">Edit {userType}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div>
              <Label className="text-sm text-white">Full name</Label>
              <Input value={editForm.full_name} onChange={(e) => setEditForm((p) => ({ ...p, full_name: e.target.value }))} className="text-white" />
            </div>
            <div>
              <Label className="text-sm text-white">Username</Label>
              <Input value={editForm.username} onChange={(e) => setEditForm((p) => ({ ...p, username: e.target.value }))} className="text-white" />
            </div>
            <div>
              <Label className="text-sm text-white">Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} className="text-white" />
            </div>
            <div>
              <Label className="text-sm text-white">Phone</Label>
              <Input value={editForm.phone_no} onChange={(e) => setEditForm((p) => ({ ...p, phone_no: e.target.value }))} className="text-white" />
            </div>
            {isStudent && (
              <>
                <div>
                  <Label className="text-sm text-white">Grade</Label>
                  <select 
                    style={selectStyle}
                    className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                    value={editForm.grade} 
                    onChange={(e) => setEditForm((p) => ({ ...p, grade: e.target.value, division: '' }))}
                  >
                    <option value="">Select grade</option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id}>{g.grade_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-white">Division</Label>
                  <select 
                    style={selectStyle}
                    disabled={!editForm.grade}
                    className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" 
                    value={editForm.division} 
                    onChange={(e) => setEditForm((p) => ({ ...p, division: e.target.value }))}
                  >
                    <option value="">Select division</option>
                    {divisions
                      .filter((d) => !editForm.grade || String(d.grade) === String(editForm.grade))
                      .map((d) => (
                        <option key={d.id} value={d.id}>{d.division_name}</option>
                      ))}
                  </select>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="text-white hover:text-white">Cancel</Button>
            <Button onClick={handleEditSave} disabled={savingEdit}>
              {savingEdit ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkModalOpen}
        onOpenChange={(open) => {
          setBulkModalOpen(open);
          if (!open) {
            setBulkFile(null);
            setBulkResult(null);
          }
        }}
      >
        <DialogContent className="bg-[hsl(var(--card-bg))] text-foreground">
          <DialogHeader>
            <DialogTitle className="text-white">Bulk import {userType}s</DialogTitle>
            <p className="text-xs text-white">Upload an .xlsx file to create multiple {userType}s at once.</p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              type="file"
              accept=".xlsx"
              onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
              className="bg-[hsl(var(--input-bg))] text-foreground"
            />
            <p className="text-xs text-white">
              Expected columns ({userType === 'student' ? 'Full name, Username, Email (optional), Phone number (optional), Grade name, Division name' : 'Full name, Username, Email (optional), Phone number (optional)'}).
            </p>
            {bulkResult && (
              <div className="rounded-md border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] p-3 text-sm space-y-2">
                <p className="font-semibold text-white">Import summary</p>
                <p className="text-white">Processed: {bulkResult.processed || 0}</p>
                <p className="text-white">Created: {bulkResult.created || 0}</p>
                <p className="text-white">Failed: {bulkResult.failed || 0}</p>
                {bulkResult.errors?.length > 0 && (
                  <div className="space-y-1">
                    {bulkResult.errors.map((err, idx) => (
                      <p key={`${err.row}-${idx}`} className="text-xs text-red-400">Row {err.row}: {err.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkModalOpen(false)} disabled={bulkUploading} className="text-white hover:text-white">Cancel</Button>
            <Button onClick={handleBulkUpload} disabled={bulkUploading || !bulkFile}>
              {bulkUploading ? 'Uploading…' : 'Upload & import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={!!confirmDeleteUser}
        onOpenChange={(open) => !open && setConfirmDeleteUser(null)}
        title="Delete user"
        description={confirmDeleteUser ? `Delete ${confirmDeleteUser.full_name || 'this user'}? This cannot be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDeleteUser(null)}
      />

      <ConfirmationModal
        isOpen={!!confirmResetUser}
        onOpenChange={(open) => !open && setConfirmResetUser(null)}
        title="Reset password"
        description={confirmResetUser ? `Reset password for ${confirmResetUser.full_name || 'this user'} to default (${confirmResetUser.user_type === 'student' ? 'Student@123' : 'Teacher@123'})?` : ''}
        confirmLabel="Reset password"
        cancelLabel="Cancel"
        onConfirm={handleResetPasswordConfirmed}
        onCancel={() => setConfirmResetUser(null)}
      />

      {/* Teacher assignments modal */}
      <Dialog
        open={assignModalOpen}
        onOpenChange={(open) => {
          setAssignModalOpen(open);
          if (!open) {
            setAssigningUser(null);
            setEditingAssignmentId(null);
          }
        }}
      >
        <DialogContent className="bg-[hsl(var(--card-bg))] text-foreground max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Teaching assignments</DialogTitle>
            <p className="text-xs text-muted-foreground">Select a grade, then a subject, and finish by choosing chapters and divisions with checkboxes.</p>
          </DialogHeader>

          <div className="space-y-4 py-2 overflow-y-auto flex-1">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-muted-foreground">Mirror of the teacher portal experience: build assignments per grade and subject with chapters and divisions.</p>
              <div className="flex items-center gap-2">
                {assignmentsLoading && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading
                  </span>
                )}
                <Button size="sm" onClick={addAssignmentRow} disabled={assignmentsLoading} className="bg-white text-black hover:bg-gray-100">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Assignment
                </Button>
              </div>
            </div>

            {assignmentsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading assignments...
              </div>
            ) : assignments.length === 0 ? (
              <div className="rounded-md border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] p-4 text-center text-sm text-muted-foreground">
                No assignments yet. Click "Add Assignment" to begin.
              </div>
            ) : (
              <div className="space-y-4">
                {assignments.map((a) => {
                  const gradeName = a.gradeName || grades.find((g) => String(g.id) === String(a.grade))?.grade_name || '';
                  const subjectName = a.subjectName || (a.subjectsOptions || []).find((s) => String(s.id) === String(a.subject))?.subject_name || '';
                  const availableDivisions = divisions.filter((d) => {
                    const gradeValue = d.grade ?? d.grade_id;
                    return a.grade ? String(gradeValue) === String(a.grade) : false;
                  });
                  const availableChapters = a.chaptersOptions || [];
                  const selectedChapters = a.chapters || [];
                  const selectedDivisions = a.divisions || [];
                  const isEditing = editingAssignmentId === a.id || !a.isExisting;

                  return (
                    <div key={a.id} className="rounded-lg border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] p-4 space-y-4">
                      {a.isExisting && (
                        <div className="flex items-center justify-between pb-3 border-b border-[hsl(var(--card-border))]">
                          <div>
                            <p className="text-sm font-semibold text-white">Assignment</p>
                            <p className="text-xs text-white/80">{gradeName || 'Grade'}{subjectName ? ` - ${subjectName}` : ''}</p>
                          </div>
                          {!isEditing ? (
                            <Button size="sm" onClick={() => handleEditAssignment(a.id)} className="bg-white text-black hover:bg-gray-100">
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </Button>
                          ) : (
                            <Button size="sm" onClick={handleCancelEdit} className="bg-white text-black hover:bg-gray-100">
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel
                            </Button>
                          )}
                        </div>
                      )}

                      {a.isExisting && !isEditing ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs font-medium text-white/70 mb-1">Grade</p>
                              <p className="text-sm font-semibold text-white">{gradeName || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-white/70 mb-1">Subject</p>
                              <p className="text-sm font-semibold text-white">{subjectName || '—'}</p>
                            </div>
                          </div>
                          {selectedChapters.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-white/70 mb-2">Chapters ({selectedChapters.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {availableChapters
                                  .filter((ch) => selectedChapters.includes(String(ch.id)))
                                  .map((chapter) => (
                                    <Badge key={chapter.id} variant="secondary" className="bg-blue-600 text-white">
                                      Chapter {chapter.order}: {chapter.chapter_name}
                                    </Badge>
                                  ))}
                              </div>
                            </div>
                          )}
                          {selectedDivisions.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-white/70 mb-2">Divisions ({selectedDivisions.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {availableDivisions
                                  .filter((d) => selectedDivisions.includes(String(d.id)))
                                  .map((division) => (
                                    <Badge key={division.id} variant="secondary" className="bg-emerald-600 text-white">
                                      Division {division.division_name}
                                    </Badge>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-sm font-medium text-white">Grade *</Label>
                              <Select
                                value={a.grade ? String(a.grade) : undefined}
                                onValueChange={(value) => handleAssignmentGradeChange(a.id, value)}
                              >
                                <SelectTrigger className="text-white">
                                  <SelectValue placeholder="Select grade" />
                                </SelectTrigger>
                                <SelectContent>
                                  {grades.map((g) => (
                                    <SelectItem key={g.id} value={String(g.id)}>{g.grade_name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-white">Subject *</Label>
                              <Select
                                value={a.loadingSubjects ? undefined : (a.subject ? String(a.subject) : undefined)}
                                onValueChange={(value) => handleAssignmentSubjectChange(a.id, value)}
                                disabled={!a.grade || a.loadingSubjects}
                              >
                                <SelectTrigger className="text-white">
                                  <SelectValue>
                                    {a.loadingSubjects ? 'Loading subjects...' : (a.grade ? (subjectName || 'Select subject') : 'Select grade first')}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {a.loadingSubjects ? (
                                    <SelectItem value="loading" disabled>
                                      <div className="flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Loading subjects...
                                      </div>
                                    </SelectItem>
                                  ) : (a.subjectsOptions || []).length > 0 ? (
                                    (a.subjectsOptions || []).map((s) => (
                                      <SelectItem key={s.id} value={String(s.id)}>{s.subject_name}</SelectItem>
                                    ))
                                  ) : (
                                    <SelectItem value="no-subjects" disabled>Select grade first</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end gap-2">
                              {!a.isExisting && (
                                <Button variant="destructive" size="sm" className="w-full" onClick={() => removeAssignmentRow(a.id)}>
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className="p-3 bg-[hsl(var(--card-bg))] rounded-md border border-[hsl(var(--card-border))]">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-sm font-medium text-white">Divisions</Label>
                              {availableDivisions.length > 0 && (
                                <label className="flex items-center gap-2 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4"
                                    checked={availableDivisions.every((d) => selectedDivisions.includes(String(d.id))) && availableDivisions.length > 0}
                                    onChange={() => handleSelectAllDivisions(a.id)}
                                  />
                                  <span className="font-semibold text-blue-600">Select All</span>
                                </label>
                              )}
                            </div>
                            <p className="text-xs text-white/70 mb-3">Choose specific divisions for this assignment, or leave empty for all divisions in the grade.</p>
                            {availableDivisions.length > 0 ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                                {availableDivisions.map((division) => (
                                  <label key={division.id} className="flex items-start gap-2 p-2 hover:bg-[hsl(var(--sidebar-hover))] rounded-md cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="mt-1 w-4 h-4"
                                      checked={selectedDivisions.includes(String(division.id))}
                                      onChange={() => toggleDivisionSelection(a.id, division.id)}
                                    />
                                    <span className="text-sm font-medium text-white">Division {division.division_name}</span>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-white/70 italic">No divisions available for this grade.</div>
                            )}
                          </div>

                          {a.subject && (
                            <div className="p-3 bg-[hsl(var(--card-bg))] rounded-md border border-[hsl(var(--card-border))]">
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-sm font-medium text-white">Chapters *</Label>
                                {!a.loadingChapters && (a.chaptersOptions || []).length > 0 && (
                                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4"
                                      checked={(a.chaptersOptions || []).every((c) => selectedChapters.includes(String(c.id))) && (a.chaptersOptions || []).length > 0}
                                      onChange={() => handleSelectAllChapters(a.id)}
                                    />
                                    <span className="font-semibold text-blue-600">Select All</span>
                                  </label>
                                )}
                              </div>
                              {a.loadingChapters ? (
                                <div className="flex items-center justify-center py-4 text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                  Loading chapters...
                                </div>
                              ) : (a.chaptersOptions || []).length > 0 ? (
                                <>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                                    {availableChapters.map((chapter) => (
                                      <label key={chapter.id} className="flex items-start gap-2 p-2 hover:bg-[hsl(var(--sidebar-hover))] rounded-md cursor-pointer">
                                        <input
                                          type="checkbox"
                                          className="mt-1 w-4 h-4"
                                          checked={selectedChapters.includes(String(chapter.id))}
                                          onChange={() => toggleChapterSelection(a.id, chapter.id)}
                                        />
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-white">Chapter {chapter.order}</p>
                                          <p className="text-xs text-white/70">{chapter.chapter_name}</p>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                  {selectedChapters.length > 0 && (
                                    <p className="text-xs text-white/70 mt-2">{selectedChapters.length} chapter(s) selected</p>
                                  )}
                                </>
                              ) : (
                                <div className="text-xs text-white/70 italic">No chapters available for this subject.</div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 flex-shrink-0 border-t border-[hsl(var(--card-border))] pt-4 mt-4">
            <Button onClick={() => setAssignModalOpen(false)} disabled={savingAssignments} className="bg-white text-black hover:bg-gray-100">Cancel</Button>
            <Button onClick={handleAssignmentsSave} disabled={savingAssignments || assignmentsLoading || assignments.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white">
              {savingAssignments ? 'Saving…' : 'Save assignments'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
